import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useRoute } from "wouter";
import { StatusBadge } from "@/components/shared/status-badge";
import { SandboxedHtml } from "@/components/shared/sandboxed-html";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { formatDateTime, formatRelativeTime } from "@/lib/format";
import { useState } from "react";
import type { Ticket } from "@db/schema";

interface Message {
  id: string;
  from?: Array<{ name?: string; email?: string }>;
  date?: number;
  body?: string;
  subject?: string;
}

export default function ClientTicketDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/portal/tickets/:id");
  const ticketId = params?.id;
  const [replyBody, setReplyBody] = useState("");

  const { data: ticket, isLoading: ticketLoading } = useQuery<Ticket>({
    queryKey: [`/api/portal/tickets/${ticketId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticketId,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: [`/api/portal/tickets/${ticketId}/messages`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticketId,
    refetchInterval: 30_000,
  });

  const replyMutation = useMutation({
    mutationFn: async (body: string) => {
      const res = await apiRequest(
        "POST",
        `/api/portal/tickets/${ticketId}/reply`,
        { htmlBody: body },
      );
      return await res.json();
    },
    onSuccess: () => {
      setReplyBody("");
      queryClient.invalidateQueries({
        queryKey: [`/api/portal/tickets/${ticketId}/messages`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/portal/tickets/${ticketId}`],
      });
      toast({ title: "Reply sent" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to send reply",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function sendReply() {
    if (!replyBody.trim()) return;
    replyMutation.mutate(replyBody);
  }

  if (ticketLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-[300px] rounded-lg" />
          <Skeleton className="h-[400px] rounded-lg lg:col-span-2" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate("/portal/tickets")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tickets
        </Button>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Ticket not found</h2>
          <p className="text-muted-foreground mt-1">
            The requested ticket could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/portal/tickets")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {ticket.key && (
              <span className="font-mono text-sm text-muted-foreground">
                {ticket.key}
              </span>
            )}
            <StatusBadge type="ticket" status={ticket.status} />
            <StatusBadge type="priority" status={ticket.priority} />
          </div>
          <h1 className="text-xl font-bold font-heading mt-1 truncate">
            {ticket.subject ?? "No subject"}
          </h1>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column - Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Key */}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Key</dt>
              <dd className="mt-0.5 text-sm font-mono">
                {ticket.key ?? "\u2014"}
              </dd>
            </div>

            {/* Status */}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Status
              </dt>
              <dd className="mt-1">
                <StatusBadge type="ticket" status={ticket.status} />
              </dd>
            </div>

            {/* Priority */}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Priority
              </dt>
              <dd className="mt-1">
                <StatusBadge type="priority" status={ticket.priority} />
              </dd>
            </div>

            {/* Created */}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Created
              </dt>
              <dd className="mt-0.5 text-sm">
                {formatDateTime(ticket.createdAt)}
              </dd>
            </div>

            {/* Last Message */}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Last Message
              </dt>
              <dd className="mt-0.5 text-sm">
                {formatRelativeTime(ticket.lastMessageAt)}
              </dd>
            </div>
          </CardContent>
        </Card>

        {/* Right column - Messages + Reply */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-heading">Messages</CardTitle>
            </CardHeader>
            <CardContent>
              {messagesLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 rounded-md" />
                  ))}
                </div>
              ) : !messages?.length ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No messages yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className="rounded-md border p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">
                          {msg.from?.[0]?.name ||
                            msg.from?.[0]?.email ||
                            "Unknown"}
                        </span>
                        <span className="text-muted-foreground">
                          {formatDateTime(msg.date)}
                        </span>
                      </div>
                      {msg.body && (
                        <SandboxedHtml
                          html={
                            /<[a-z][\s\S]*>/i.test(msg.body)
                              ? msg.body
                              : msg.body.replace(/\n/g, "<br />")
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Composer */}
              <div className="border-t pt-4 mt-4 space-y-3">
                <Textarea
                  placeholder="Type your reply..."
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={sendReply}
                    disabled={replyMutation.isPending || !replyBody.trim()}
                  >
                    {replyMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <Send className="mr-2 h-4 w-4" />
                    Send Reply
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
